import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircle2, AlertTriangle, ArrowRight, RefreshCw, Home,
  Mail, Info, Clock,
} from 'lucide-react';

export interface BookingResultResponse {
  success?: boolean;
  bookingConfirmed?: boolean;
  paymentRefunded?: boolean;
  title?: string;
  message?: string;
  refundMessage?: string;
  nextStep?: string;
  contact?: string;
  error?: string;
  reason?: string;
  supportContact?: boolean;
}

type ResultKind = 'success' | 'conflict' | 'unknown';

function resolveKind(result: BookingResultResponse | null): ResultKind {
  if (!result) return 'unknown';
  if (result.success === true && result.bookingConfirmed !== false) return 'success';
  if (result.success === false || result.bookingConfirmed === false) return 'conflict';
  return 'unknown';
}

interface Props {
  result: BookingResultResponse | null;
  /** Show a 5-second redirect countdown on success */
  autoRedirect?: boolean;
  redirectUrl?: string;
  /** Extra detail rows shown on success (court, date, name …) */
  detailRows?: { icon: React.ElementType; label: string; value: string }[];
  onNavigateBooking?: () => void;
}

export default function BookingResultView({
  result,
  autoRedirect = true,
  redirectUrl = 'https://svheinersreuth-padel.de/',
  detailRows,
  onNavigateBooking,
}: Props) {
  const kind = resolveKind(result);
  const [progress, setProgress] = useState(0);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    if (kind !== 'success' || !autoRedirect) return;
    const duration = 5000;
    const step = 50;
    const inc = (step / duration) * 100;
    const id = setInterval(() => {
      setProgress(prev => {
        const next = prev + inc;
        if (next >= 100) {
          clearInterval(id);
          if (!redirecting) {
            setRedirecting(true);
            window.location.href = redirectUrl;
          }
          return 100;
        }
        return next;
      });
    }, step);
    return () => clearInterval(id);
  }, [kind, autoRedirect, redirectUrl, redirecting]);

  const doRedirect = () => {
    if (redirecting) return;
    setRedirecting(true);
    window.location.href = redirectUrl;
  };

  const doBookingRedirect = () => {
    if (onNavigateBooking) {
      onNavigateBooking();
    } else {
      window.location.href = '/?page=booking';
    }
  };

  return (
    <div className="min-h-screen svh-canvas">
      <div className="min-h-screen flex flex-col items-center justify-center px-4 py-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-[560px]"
        >
          {/* Brand header */}
          <div className="text-center mb-8">
            <img
              src="/photos/Image.jpeg"
              alt="SV Heinersreuth"
              className="h-14 w-14 rounded-full object-cover mx-auto mb-3 shadow-sm ring-1 ring-black/5"
            />
            <p className="text-[15px] font-bold tracking-tight" style={{ color: 'var(--svh-ink)' }}>
              SV Heinersreuth
            </p>
            <p className="svh-label mt-1">Padel &amp; Tennis</p>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            className="svh-card svh-card-lg svh-card-raised overflow-hidden"
          >
            <div className="h-1 w-full" style={{ background: 'var(--svh-crest)' }} />
            <div className="p-8 md:p-10">

              {kind === 'success' && (
                <SuccessContent
                  title={result?.title || 'Buchung erfolgreich'}
                  message={result?.message || 'Vielen Dank. Ihre Buchung wurde bestätigt.'}
                  detailRows={detailRows}
                  autoRedirect={autoRedirect}
                  progress={progress}
                  redirecting={redirecting}
                  onRedirect={doRedirect}
                  onBookingRedirect={doBookingRedirect}
                />
              )}

              {kind === 'conflict' && (
                <ConflictContent
                  title={result?.title || 'Zeitslot bereits vergeben'}
                  message={result?.message || 'Dieser Zeitslot ist leider nicht mehr verfügbar.'}
                  paymentRefunded={result?.paymentRefunded}
                  refundMessage={result?.refundMessage}
                  nextStep={result?.nextStep}
                  supportContact={result?.supportContact}
                  contact={result?.contact}
                  onBookingRedirect={doBookingRedirect}
                  onRedirect={doRedirect}
                />
              )}

              {kind === 'unknown' && (
                <UnknownContent onRedirect={doRedirect} />
              )}
            </div>
          </motion.div>

          <p className="svh-meta text-center mt-6">SV Heinersreuth e.V.</p>
        </motion.div>
      </div>
    </div>
  );
}

// ── Success ────────────────────────────────────────────────────────────────────

function SuccessContent({
  title, message, detailRows, autoRedirect, progress, redirecting,
  onRedirect, onBookingRedirect,
}: {
  title: string; message: string;
  detailRows?: { icon: React.ElementType; label: string; value: string }[];
  autoRedirect: boolean; progress: number; redirecting: boolean;
  onRedirect: () => void; onBookingRedirect: () => void;
}) {
  return (
    <>
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, delay: 0.2 }}
        className="flex items-center justify-center mb-6"
      >
        <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center">
          <CheckCircle2 className="w-8 h-8 text-emerald-600" strokeWidth={1.75} />
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.25 }}
        className="text-center mb-8"
      >
        <p className="text-[12px] font-bold tracking-[0.16em] uppercase text-emerald-700 mb-3">
          Buchung bestätigt
        </p>
        <h1 className="svh-h2 mb-4">{title}</h1>
        <p className="svh-body">{message}</p>
      </motion.div>

      {detailRows && detailRows.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
          className="rounded-[10px] overflow-hidden mb-4"
          style={{ background: 'var(--svh-surface-sub)', border: '1px solid var(--svh-border)' }}
        >
          <div className="px-5 pt-4 pb-2">
            <p className="svh-label">Buchungsdetails</p>
          </div>
          <div className="px-5 pb-4 space-y-3">
            {detailRows.map(({ icon: Icon, label, value }) => (
              <div key={label} className="flex items-start gap-3">
                <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--svh-ink-3)' }} strokeWidth={2} />
                <div className="min-w-0">
                  <p className="svh-meta mb-0.5">{label}</p>
                  <p className="text-[14px] font-semibold break-words" style={{ color: 'var(--svh-ink)' }}>{value}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.35 }}
        className="rounded-[10px] overflow-hidden mb-6"
        style={{ border: '1px solid var(--svh-border)' }}
      >
        <div className="flex items-start gap-3 px-5 py-4 bg-emerald-50/60">
          <Mail className="w-4 h-4 text-emerald-700 flex-shrink-0 mt-0.5" strokeWidth={2} />
          <p className="text-[13px] leading-relaxed" style={{ color: 'var(--svh-ink-2)' }}>
            Eine Bestätigungs-E-Mail trifft in Kürze in Ihrem Postfach ein.
          </p>
        </div>
        <div className="flex items-start gap-3 px-5 py-4 bg-amber-50/70 border-t" style={{ borderColor: 'var(--svh-border)' }}>
          <span className="text-[15px] flex-shrink-0" role="img" aria-label="Spam">📬</span>
          <p className="text-[13px] leading-relaxed text-amber-900">
            <span className="font-semibold">Keine E-Mail erhalten?</span> Bitte überprüfen Sie auch Ihren <span className="font-semibold">Spam- oder Junk-Ordner</span> — manchmal landet unsere Mail dort.
          </p>
        </div>
      </motion.div>

      {autoRedirect && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3, delay: 0.4 }} className="mb-6">
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--svh-border)' }}>
            <div
              className="h-full bg-emerald-500"
              style={{ width: `${progress}%`, transition: 'none' }}
            />
          </div>
          <p className="svh-meta text-center mt-2.5">
            Sie werden in 5 Sekunden automatisch weitergeleitet.
          </p>
        </motion.div>
      )}

      <hr className="svh-rule mb-6" />

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.45 }} className="space-y-3">
        <button
          onClick={onRedirect}
          disabled={redirecting}
          className="svh-btn svh-btn--primary svh-focus w-full"
        >
          Jetzt zur Startseite
          <ArrowRight className="w-4 h-4" />
        </button>
        <button
          onClick={onBookingRedirect}
          className="svh-btn svh-btn--secondary svh-focus w-full"
        >
          Zurück zur Buchung
        </button>
      </motion.div>
    </>
  );
}

// ── Conflict ───────────────────────────────────────────────────────────────────

function ConflictContent({
  title, message, paymentRefunded, refundMessage, nextStep, supportContact, contact,
  onBookingRedirect, onRedirect,
}: {
  title: string; message: string;
  paymentRefunded?: boolean; refundMessage?: string; nextStep?: string; supportContact?: boolean; contact?: string;
  onBookingRedirect: () => void; onRedirect: () => void;
}) {
  return (
    <>
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, delay: 0.2 }}
        className="flex items-center justify-center mb-6"
      >
        <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center">
          <AlertTriangle className="w-8 h-8 text-amber-600" strokeWidth={1.75} />
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.25 }}
        className="text-center mb-8"
      >
        <h1 className="svh-h2 mb-4">{title}</h1>
        <p className="svh-body">{message}</p>
      </motion.div>

      {refundMessage && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
          className="rounded-[10px] px-5 py-4 mb-4"
          style={{ background: 'var(--svh-surface-sub)', border: '1px solid var(--svh-border)' }}
        >
          <div className="flex items-start gap-3">
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--svh-ink-3)' }} strokeWidth={2} />
            <div>
              <p className="svh-label mb-1">
                {paymentRefunded ? 'Rückerstattung' : 'Zahlung'}
              </p>
              <p className="text-[14px] leading-relaxed" style={{ color: 'var(--svh-ink)' }}>
                {refundMessage}
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {nextStep && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.35 }}
          className="rounded-[10px] px-5 py-4 mb-6"
          style={{ background: 'var(--svh-surface-sub)', border: '1px solid var(--svh-border)' }}
        >
          <div className="flex items-start gap-3">
            <Clock className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--svh-ink-3)' }} strokeWidth={2} />
            <p className="text-[14px] leading-relaxed" style={{ color: 'var(--svh-ink-2)' }}>{nextStep}</p>
          </div>
        </motion.div>
      )}

      <hr className="svh-rule mb-6" />

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.4 }} className="space-y-3">
        <button
          onClick={onBookingRedirect}
          className="svh-btn svh-btn--primary svh-focus w-full"
        >
          <RefreshCw className="w-4 h-4" />
          Neue Zeit auswählen
        </button>
        {(supportContact || contact) && (
          <button
            onClick={() => window.location.href = '/?page=contact'}
            className="svh-btn svh-btn--secondary svh-focus w-full"
          >
            <Mail className="w-4 h-4" />
            Support kontaktieren
          </button>
        )}
        <button
          onClick={onRedirect}
          className="svh-btn svh-btn--secondary svh-focus w-full"
        >
          <Home className="w-4 h-4" />
          Zur Startseite
        </button>
      </motion.div>
    </>
  );
}

// ── Unknown / ambiguous ────────────────────────────────────────────────────────

function UnknownContent({ onRedirect }: { onRedirect: () => void }) {
  return (
    <>
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, delay: 0.2 }}
        className="flex items-center justify-center mb-6"
      >
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center"
          style={{ background: 'var(--svh-surface-sub)', border: '1px solid var(--svh-border)' }}
        >
          <Info className="w-8 h-8" style={{ color: 'var(--svh-ink-3)' }} strokeWidth={1.75} />
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.25 }}
        className="text-center mb-8"
      >
        <p className="svh-label mb-3">Status offen</p>
        <h1 className="svh-h2 mb-4">
          Buchungsstatus konnte nicht eindeutig geprüft werden
        </h1>
        <p className="svh-body">
          Wir konnten den Status Ihrer Buchung nicht eindeutig ermitteln.
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.3 }}
        className="rounded-[10px] px-5 py-4 mb-6 bg-amber-50/70"
        style={{ border: '1px solid var(--svh-border)' }}
      >
        <div className="flex items-start gap-3">
          <Mail className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" strokeWidth={2} />
          <p className="text-[14px] leading-relaxed text-amber-900">
            Falls Sie eine Zahlungsbestätigung erhalten haben, kontaktieren Sie bitte{' '}
            <span className="font-semibold">SV Heinersreuth</span>. Wir prüfen Ihre Buchung umgehend.
          </p>
        </div>
      </motion.div>

      <hr className="svh-rule mb-6" />

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.35 }}>
        <button
          onClick={onRedirect}
          className="svh-btn svh-btn--primary svh-focus w-full"
        >
          Zur Startseite
          <ArrowRight className="w-4 h-4" />
        </button>
      </motion.div>
    </>
  );
}
